import { type DatabaseSdkContract } from '@databases/sdk';
import { type DatabaseView } from '@databases/types/database/view';
import { type ViewLayoutRegistry } from '@databases/view-layout-modules';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';

interface RendererDatabase {
  readonly sdk: {
    readonly views: Pick<DatabaseSdkContract['views'], 'getViewLayoutType'>;
  };
  readonly registry: Pick<ViewLayoutRegistry, 'isWritable'>;
}

/**
 * Selected view and resolved renderer layout used for lifecycle comparisons.
 */
export interface CurrentViewIdentity {
  /**
   * Selected view ID within the active bridge database.
   */
  readonly id: string;

  /**
   * Resolved renderer layout, including supported extension layouts. Before
   * source attachment, the reader uses the current view settings.
   */
  readonly layoutType: string;
}

/**
 * Only Table V2 has an extension renderer. Writable data modules alone do not
 * establish renderer support; built-in drafts and unattached views stay view-owned.
 */
export function getCurrentViewLayoutType(
  view: Pick<DatabaseView, 'id' | 'settings'>,
  database: RendererDatabase | undefined,
): string {
  const layoutType = view.id && database?.sdk.views.getViewLayoutType(view.id);

  if (layoutType === TABLE_V2_LAYOUT_ID && database?.registry.isWritable(layoutType)) {
    return layoutType;
  }

  return view.settings.layout.type;
}

/**
 * Shares one identity source across observation and consumer setup. The injected
 * view reader must read current ID/layout fields without materializing a database.
 */
export function createCurrentViewIdentityReader({
  getView,
  getDatabase,
}: {
  getView: () => Pick<DatabaseView, 'id' | 'settings'>;
  getDatabase: (contentId: string) => RendererDatabase | undefined;
}): (contentId: string) => CurrentViewIdentity {
  return (contentId) => {
    const view = getView();

    return {
      id: view.id,
      layoutType: getCurrentViewLayoutType(view, getDatabase(contentId)),
    };
  };
}
